/**
 * The complete built-in response table (R11 extraction) — one line per slot,
 * engine-neutral register.
 *
 * @remarks
 * Extracted from `default-responses.ts` when the R11 `storyHasEnded` slot
 * pushed that file past the 500-line cap: the TABLE is content, the machinery
 * around it (speaking, pools, usage) is code, and the cap said out loud that
 * they were two files pretending to be one.
 *
 * @packageDocumentation
 */

import type { DefaultResponseSlot, NarrativeLine } from '@orpheus/content-schema';

/** Build one standard-priority line (the defaults never shout). */
const standardLine = (source: NarrativeLine['source'], text: string): NarrativeLine => ({
	priority: 'standard',
	source,
	text,
});

/**
 * The complete built-in table — one line per slot, engine-neutral register.
 *
 * @remarks
 * D42 (Calder's Lane A pass, landed 2026-08-19) rewrote the wording and moved
 * the CHANNEL of twenty slots from `system` to `environment`. The rule it
 * enforces: `system` is instrument output — a reader, a load display, a
 * slice-end stamp; things with housings. Everything the GAME says is spoken by
 * the world, a character, a person who was here, or the player. A parse teach
 * is the narrator being helpful, not a terminal reporting a fault, and the
 * browser player's `text-transform: uppercase` on `system` was SHOUTING most
 * of the engine's voice — including, after D37, its conversational questions.
 * `help` is the one built-in that stays `system`: a directive listing is
 * chrome, not narration.
 *
 * Register (Mike's 2026-08-13 ruling): character-neutral and game-agnostic,
 * NEVER flat. The yardstick is `askDeflection` — two clauses, the second
 * turning on the first. The MECHANICS of which slot fires are pinned by the
 * execution suites; the wording is the content lane's.
 */
export const BUILT_IN_RESPONSES: Readonly<Record<DefaultResponseSlot, NarrativeLine>> = {
	// D232: the actor is heard and not reached — behind a door, across glass.
	// Like `outOfReach`, the line names no barrier (the pack's
	// `unreachableNarratives` do); it states only what is true everywhere it
	// can fire: the voice carries, the hands do not.
	actorOutOfReach: standardLine('environment', 'The {itemName} is within hearing and not within reach.'),
	alreadyHeld: standardLine('environment', 'You already have the {itemName}. It does not improve with repetition.'),
	// D143: the wear family. A thing with no `equippable` is refused about the
	// THING, not the verb — `wear` is a directive now.
	alreadyWorn: standardLine('environment', 'You are already wearing the {itemName}.'),
	// D38: an ANSWER that resolves to nothing. The question stays open, so the
	// refusal keeps its context — the player is mid-conversation, not adrift,
	// and D42 says so out loud rather than leaving it implied.
	// §3.2/D84 (Calder's verdict): an answer that RESOLVED and was refused
	// fails in the QUESTION's terms — the player answered a question, so the
	// refusal names what the answer was an answer to. `{tokens}` here is the
	// resolved instrument's AUTHORED name (the rejection carries both item
	// ids), never the raw input — no quoting, no article surgery.
	answerDoesNotServe: standardLine('environment', 'The {tokens} will not {verb} the {itemName}.'),
	answerNotUnderstood: standardLine(
		'environment',
		'You cannot {verb} the {itemName} {joiner} {tokens} — and the question still stands.',
	),
	// Character-neutral by D25 (the eurydice-voiced deflection lives in the
	// Orpheus pack's override, where a named character belongs) — but neutral
	// is a REGISTER, not a license to be flat: the built-ins are what a
	// zero-authoring game ships with, so they carry their own weight (ruled by
	// Mike, 2026-08-13). This line is that ruling's worked example; do not
	// "improve" it.
	askDeflection: standardLine('environment', 'No answer comes, and the silence feels deliberate.'),
	// D37: after describing a thing the player merely named, ask what they want
	// done with it — the difference between a lookup and a conversation. The
	// line acknowledges that the player SAID something, which is the whole
	// point; a bare form field would not.
	bareNounPrompt: standardLine('environment', 'The {itemName}, then. What would you like done with it?'),
	blankInput: standardLine('environment', 'Nothing arrived that the world can act on. HELP lists what would.'),
	// §8.8 (Calder's field study, F6): a stopped chain names what it abandoned
	// — the player typed three things and should never wonder whether the
	// third ran silently or never ran at all.
	// R25: a take refused because the hands are full — `drop` is the relief
	// valve, and the line says so without naming a number.
	capacityExceeded: standardLine('environment', 'Your hands are full. Something must be set down before the {itemName} comes along.'),
	chainAbandoned: standardLine('environment', 'The rest went undone: {abandoned}.'),
	// R17-prep: the which-one prompt beside the clarification chips. Rendered
	// by the app as conversational chrome (the R6 ruling: the question is not
	// transcript), so the SOURCE here styles nothing today — `system` records
	// that it is instrument voice. D229 (E14): the line names what it offers
	// — the chips did, the line did not, and a screen reader or the terminal
	// hears only the line.
	clarificationQuestion: standardLine('system', 'WHICH? {options}'),
	conditionUnmet: standardLine('environment', 'Nothing gives. Either the moment is wrong or it has already passed.'),
	// D212: the fuzzy rung read the head verb as a different act — a verb the
	// player did not type is theirs to confirm (Mike's ruling). System voice,
	// like the restart gate: the terminal asking, not the world.
	correctionConfirm: standardLine('system', 'Did you mean {correction}?'),
	correctionKeepLabel: standardLine('system', 'NO'),
	correctionKept: standardLine('system', 'Left as typed.'),
	correctionProceedLabel: standardLine('system', 'YES'),
	deviceIsDead: standardLine('environment', 'The switch goes over. Nothing in the {itemName} answers it.'),
	// D135: D120's rule for the door verbs — a door already where it was asked
	// to be did not move, and must not borrow the prose of the movement.
	doorAlreadyClosed: standardLine('environment', 'The {itemName} is already shut.'),
	doorAlreadyOpen: standardLine('environment', 'The {itemName} is already open.'),
	// D135: the EVENT of a door moving, when the pack authored nothing — flat
	// on purpose, as `switchedOn` is: the state description is `look`'s to
	// speak, and reading it aloud as the event was the defect (Calder's §3).
	doorClosed: standardLine('environment', 'You close the {itemName}.'),
	doorOpened: standardLine('environment', 'You open the {itemName}.'),
	// R25: a successful drop — an item's authored `dropNarratives` outrank it.
	dropAcknowledgement: standardLine('environment', 'You set the {itemName} down.'),
	// D226 (E8): the room's description never knew what the player set down
	// in it, so a dropped flask vanished from every later `look`. The engine
	// owns the sentence; `{itemNames}` comes in as a sentence list ("the flask
	// and the rope"), so the line reads the same for one thing or three.
	droppedHere: standardLine('environment', 'Still here, where you set {itemNames} down.'),
	// D212 (Mike, 2026-09-25: "Nothing here answers to" was not natural): a
	// thing the pack declares somewhere, not here — the honest plain answer.
	entityNotFound: standardLine('environment', 'You see no {tokens} here.{suggestions}'),
	entityNotVisible: standardLine('environment', 'It is not here — not where you are standing, anyway.'),
	// D212: a word the pack never declared a thing by — D48's distinction
	// (the kettle elsewhere vs "happening"), extended from questions to every
	// miss. The world does not deny a thing it has; it denies a word it lacks.
	entityUnknown: standardLine('environment', 'What {tokens}?{suggestions}'),
	equipped: standardLine('environment', 'You put on the {itemName}.'),
	exitLeadsOutward: standardLine('environment', 'That way is closed, and it was closed before you arrived.'),
	// D234: the fight's facts, ten slots across this table. Each is one
	// sentence a pack re-voices — the facility stamps a reading, a tavern
	// counts in blood — and each says the fact and nothing the arithmetic
	// did not earn: no gauge, no adjective. The foe's own lines outrank the
	// three here that speak for it (`attackNarratives`, `defeatNarratives`).
	foeAlreadyDefeated: standardLine('environment', 'The {itemName} is past that. It is not getting up.'),
	foeDefeated: standardLine('environment', 'The {itemName} goes down, and stays down.'),
	foeStrikes: standardLine('environment', 'The {itemName} comes at you, and it is faster than it looked.'),
	gibberishInput: standardLine(
		'environment',
		'Nothing in "{tokens}" reads as language at all. HELP lists what does.{suggestions}',
	),
	// D28: TRUE keyboard mash — no token resembled language at all. Packs may
	// claim this slot for their gibberish egg without touching the real-English
	// no-directive teach below. `{suggestions}` still applies: even mash
	// sometimes lands a near miss.
	// D235: a thing offered to a body with no arm for it. The actor's own
	// `refusesGiftsNarratives` is where the sentence lives ("There is no gap
	// under it."); this is the floor, and it names the THING, because the
	// thing is what the player is holding out.
	giftRefused: standardLine('environment', 'You hold out the {itemName}. Nothing takes it, and the moment passes.'),
	handsAreNotEnough: standardLine('environment', 'Hands alone do not do it for the {itemName}.'),
	handsDescription: standardLine('environment', 'Your hands. Empty, intact, and increasingly experienced.'),
	// D43: the listing is DERIVED from the compiled lexicon (see
	// `renderDirectiveListing`) — a hardcoded verb list ships a lying help
	// screen in any game whose vocabulary differs, and after D35 declared
	// eighteen unsupported verbs it was already lying in this one. A pack may
	// still re-voice the frame and keep the derived list by embedding
	// `{directives}`.
	help: standardLine('system', 'AVAILABLE DIRECTIVES: {directives}'),
	// D37 parks all three `missing*` slots as pending completions, so each is
	// now literally a QUESTION the engine will accept an answer to. The second
	// sentence makes the answer shape explicit, because a question the engine
	// means should look like one.
	// D269: said before the lines of a `use` the player did not name — the one carried thing that opens what they named.
	impliedInstrument: standardLine('environment', '(with the {itemName})'),
	inventoryEmpty: standardLine('environment', 'You are carrying nothing at all.'),
	inventoryList: standardLine('environment', 'You are carrying: {itemNames}'),
	// D152: the generic refusal for a spent thing — names the fact, not the
	// fiction; a pack that knows what broke says so in `spentRefusalNarratives`.
	itemSpent: standardLine('environment', 'The {itemName} is past that now.'),
	// D179: the chrome directives, in the terminal's own register — a
	// capitalized label, then a plain sentence. The three `automatic` lines
	// are Calder's (2026-09-15); they moved here from the POC's reaction
	// table the day the words entered the vocabulary, because a parse that
	// succeeds never consults that table.
	// D189: the three said "this descent" — the POC's word for its own game — and
	// the POC now says it as an override; a pack that says nothing hears the
	// engine's word, which is "game".
	loadAutomatic: standardLine('system', 'NOTHING TO LOAD. This game restores itself, and there is only ever one of it.'),
	// D185: the other half of the answer LOAD needed — the host finds no slot
	// and re-answers with this. Calder's line (2026-09-17): "moment" is one
	// word for one thing across all three save lines — SAVE makes a moment,
	// LOAD returns you to it, NOTHING SAVED says there isn't one yet.
	// D231 (Calder's P7 §3): the host keeps nothing — the terminal in a 404
	// page, the marketing embed — and says so, so the pack need not know
	// where it is hosted. System voice, like its siblings: a fact about the
	// machine, not the fiction.
	loadNothingKept: standardLine('system', 'NOTHING TO LOAD. Nothing here is kept; there is only now.'),
	loadNothingSaved: standardLine('system', 'NOTHING SAVED. There is no moment to return to yet — SAVE makes one.'),
	// D183 kept a hedge here ("if anything was") while no slot existed for the
	// other case; D185 gave it `loadNothingSaved`, and Calder dropped the hedge.
	loadRestored: standardLine('system', 'LOADING. The last moment you saved.'),
	malformedCommand: standardLine('environment', 'Those words did not arrive as an instruction. Say it another way.'),
	missingDirection: standardLine('environment', 'Go where? Name a direction and it will be taken.'),
	// D230: the player's verb opens the question. `open the locker` still asks
	// "Open it with what?"; `pry the locker` asks "Pry it with what?", and Salt
	// Debt's `leave coin` is asked "Leave it on what?" rather than "Use it on
	// what?" — the question is about the act the player named.
	missingInstrument: standardLine('environment', '{Verb} it with what? Name the thing and it will be tried.'),
	// D235: `give <thing>` with nobody named, where somebody in the game takes
	// gifts — a question, parked like `missingTarget`, answered by a bare name.
	missingRecipient: standardLine('environment', '{Verb} the {itemName} to whom? Name them and it will be offered.'),
	missingTarget: standardLine('environment', '{Verb} it on what? Name the thing and it will be tried.'),
	// D189: was "…The room is decided on the subject." — EURYDICE's register, and
	// the POC overrides this slot anyway; the built-in says only the fact.
	// D190 (Calder): the fact, with the second clause back. Stripping the voice
	// was right and stripping the SHAPE was the cost — this table's own rule is
	// character-neutral and game-agnostic, NEVER flat (Mike, 2026-08-13), and a
	// one-clause default is what a pack that authors nothing ships with. The
	// shape now rhymes with `noPathToThat`, which answers the adjacent case
	// (a named thing no way here leads to) — two neighbors in one family
	// should sound like it.
	noExitThatWay: standardLine('environment', 'Nothing leads that way. You can face it; you cannot take it.'),
	// §8.3/D80 (Calder's R6): language without a directive is a player NAMING
	// something, not failing to conjugate — so the line rhymes with the
	// entity misses deliberately instead of lecturing about verbs. D212: a
	// naming that the pack KNOWS a word of speaks `entityNotFound` ("There is
	// no kettle here."); this slot is the naming of nothing the pack ever
	// declared, and rhymes with `entityUnknown`. The slots stay distinct
	// because the diagnoses are: no verb at all here, a verb whose object was
	// not found there — a pack may voice them apart.
	noIntentRecognized: standardLine('environment', 'What {tokens}?{suggestions}'),
	noMatchingInteraction: standardLine('environment', 'Those two have nothing to say to each other.'),
	// D31: a named destination no exit in this room travels toward. D231
	// (Calder's P7 §2): D31's line stated the RULE — "leads nowhere. Doorways
	// and directions do that; things do not" — and, said of a sealed blast
	// door, told a door it was not a doorway. The built-in now asserts nothing
	// about what KIND of thing it is, only the fact, which is about HERE; the
	// second clause is `noExitThatWay`'s, its neighbor. The article is D182's.
	noPathToThat: standardLine('environment', 'No way from here leads to the {itemName}. You can name it; you cannot take it.'),
	// D29 — the three non-imperative registers. Neutral but weighted (Mike's
	// D25 follow-up ruling): a zero-authoring game still sounds deliberate.
	// R34/D114: `never mind` with nothing parked. Honest about the no-op, and
	// the second clause hands the turn back to the player.
	noQuestionToWithdraw: standardLine('environment', 'Nothing was asked. There is nothing to set aside.'),
	// D234: `attack` on a body with no `combat` block (ruling 3): the actor's
	// own `verbNarratives.attack` is where the sentence lives ("She would
	// notice."); this is the floor. And `with` a thing that is no weapon.
	notAFoe: standardLine('environment', 'The {itemName} is not something you fight. Not from where you stand.'),
	notAWeapon: standardLine('environment', 'The {itemName} is not a thing you strike with, and it would not change what happened if you did.'),
	// R25: dropping a thing the hands do not hold — and the refusal never
	// mentions searching (the player is not looking for it, they are not
	// holding it).
	notCarried: standardLine('environment', 'You are not carrying the {itemName}.'),
	// D39: never ask a question with no possible answer. When NOTHING in the
	// pack is authored to act on this thing, "with what?" is a trap; say so.
	nothingOpensThat: standardLine(
		'environment',
		'Nothing here would {verb} the {itemName}, and nothing you are carrying would either.',
	),
	// D226 (E12): `again` before anything has been said.
	nothingToRepeat: standardLine('environment', 'Nothing has been said yet that could be said again.'),
	// D242: `take the band from the locker` with the band elsewhere — the place
	// is a check, never a second act, so the line names only the thing. D243:
	// Calder's words, replacing the placeholder ("Not from there: …").
	notInPlaceNamed: standardLine('environment', "The {itemName} isn't there."),
	// D50: not a refusal about the verb — a fact about the thing. Closing a
	// collapsed shelf fails because the shelf has no door, and saying which is
	// the difference between an honest engine and a shrug. D135 routes lidless
	// OPEN here too (Calder's locker-bank §4): "nothing here would open the
	// benches" was the missing-instrument register speaking about a thing
	// with no lid, and it sent players hunting for a key that does not exist.
	// D215 (Calder's re-cut): the thing leads the sentence no more — "The
	// crosses has nothing on it" was the slot conjugating for a singular it
	// never sees. Number-agnostic, so the benches and the cross read alike.
	notOpenable: standardLine('environment', 'Nothing on the {itemName} opens or shuts.'),
	notPortable: standardLine('environment', 'It is part of the room, and the room is not offering.'),
	notWearable: standardLine('environment', 'The {itemName} is not something you can wear.'),
	notWorn: standardLine('environment', 'You are not wearing the {itemName}.'),
	// D252: an order a body has no arm for. Like `verbOnActor`, the floor under
	// every pack that writes no `orderFallbackNarratives`: it says the order was
	// heard and not taken, and claims nothing about why — that is the body's.
	orderRefused: standardLine('environment', 'The {itemName} does not take that order from you.'),
	// R29 (Calder's D104 §3): seen, not reached. The line claims nothing about
	// WHAT bars the way — glass, a chasm, a shelf — because the barrier is the
	// pack's to name (unreachableNarratives outrank this); the built-in states
	// only what is true everywhere the slot can fire.
	outOfReach: standardLine('environment', 'You can see the {itemName} from here. You cannot reach it.'),
	// D234: the foe's strike as a FACT, after its line — what it cost and what
	// is left, or that what is worn turned it. The system channel, because a
	// reading is what a pack re-voices it into; it is the one place the
	// built-in table stamps a figure.
	playerHurt: standardLine('system', 'INJURY — {damage} — REMAINING {health}'),
	playerUnhurt: standardLine('system', 'INJURY — NONE — TURNED'),
	// D217 (Mike, 2026-09-28): a set answered in one line — the members named, when no group speaks for them; and the one line every other verb on a set gets.
	pluralOneAtATime: standardLine('environment', 'One at a time.'),
	pluralSetNamed: standardLine('environment', '{itemNames}.'),
	questionNotUnderstood: standardLine(
		'environment',
		'A question, and nothing in view volunteers an answer. ASK ABOUT <thing> puts it to something specific.',
	),
	// R34/D114: the survives-until-answered model's SECOND loud exit — the
	// player withdrew the question themselves (`never mind`, or the chip's
	// dismiss submitting the same words). The line closes without scolding:
	// putting a question down is a choice, and it can always be asked again.
	questionSetAside: standardLine('environment', 'The question is set aside. It will keep.'),
	// D79: the survives-until-answered model's first loud exit — the question
	// was asked in a room, and the player has left the room. One line, once.
	questionWithdrawn: standardLine('environment', 'The question stays behind with the room that asked it.'),
	// D179: the gate before the descent is thrown away — the same sentence the
	// title screen's BEGIN AGAIN confirms with, because it is the same act.
	// D183: no number's plural in a sentence. `{count}` is still OFFERED to
	// this slot — a pack that wants the number re-cuts the line and gets it —
	// but the DEFAULT cannot say "1 locations", which is what it said until
	// Lyra's D179 count met a one-room save.
	// D229 (E16): the typed confirm says how it is answered — the chips are
	// not on a terminal or in a screen reader's ear — in a second line, so a
	// pack's confirm sentence (the title screen's, D179) stays its own.
	restartAnswers: standardLine('system', 'Type YES to {proceedLabel}, or NO to {keepLabel}.'),
	restartConfirm: standardLine('system', 'RESTART. This throws away everything you have walked. There is no copy.'),
	restartKeepLabel: standardLine('system', 'KEEP IT'),
	restartKept: standardLine('system', 'KEPT. The game stands where you left it.'),
	restartProceedLabel: standardLine('system', 'START OVER'),
	// D98 (Calder's retake-lie finding): `takeNarratives` were authored for
	// the item's PLACEMENT, and only the first take stands there — a re-take
	// after a drop speaks this instead, and the line says only what is true
	// anywhere: the thing was yours already.
	retakeAcknowledgement: standardLine('environment', 'You take the {itemName} back.'),
	saveAutomatic: standardLine('system', 'NOTHING TO SAVE. This game saves itself after every turn. Close the tab whenever you like; it will be here.'),
	// D183 (Calder): was "This moment is kept; LOAD brings it back." — "kept"
	// is `restartKept`'s word, doing an unrelated job in the same voice and
	// the same session, and the passive was the only one in the eleven system
	// lines. The directive is still named, which is the part that matters.
	saveNothingKept: standardLine('system', 'NOTHING IS KEPT. This lasts as long as the page does; close it and it is gone.'),
	saveWritten: standardLine('system', 'SAVED. LOAD returns you to this moment.'),
	// D50: searching that turns up nothing has to SAY so, and say it about the
	// thing. "You find nothing" is the failure state that makes searching worth
	// doing everywhere else; a flat refusal teaches the player the verb is
	// decorative (Calder's first ask, and the one that decides the verb).
	searchFoundNothing: standardLine('environment', 'You go through the {itemName} and turn up nothing you did not already have.'),
	// D229 (E18): with empty hands there is nothing to have already, and the
	// line above read as an inventory line because it was one.
	searchFoundNothingEmptyHanded: standardLine('environment', 'You go through the {itemName} and turn up nothing worth the trouble.'),
	// D247 (Mike's play of the Meridian): nothing hidden, but the thing still
	// holds what is in plain sight — the trauma kit with the hex key and the
	// sample tube in it. Measured against the hands, "nothing you did not
	// already have" was false there; the line names what is in it instead.
	searchFoundOnlyInSight: standardLine('environment', 'You go through the {itemName}: {itemNames}, and nothing else.'),
	/*
	 * R25 (Calder's §1.2): the dropped set participates in CHOOSING the slot,
	 * never decorates the chosen one — otherwise `searchRoomEmpty` says nothing
	 * is here and a tacked-on line names the thing that is. Perception
	 * register, no number agreement anywhere (the §7.7 guardrail taken where
	 * it is free): the lines read correctly for one item and for four.
	 */
	/*
	 * R30 (Calder's barred table): barrier-agnostic, no refrain — glass, a
	 * chasm, a height, and a locked case are four different sentences, and
	 * the slot cannot know which; the refrain is one pack's, not every
	 * pack's. His lines verbatim.
	 */
	searchRoomBarredAlso: standardLine('environment', 'And, in sight and out of reach: {itemNames}.'),
	searchRoomBarredOnly: standardLine(
		'environment',
		'What there is to go through here is in sight and out of reach: {itemNames}.',
	),
	searchRoomDroppedAlso: standardLine('environment', 'And, set down by your own hand and still where you put it: {itemNames}.'),
	searchRoomDroppedOnly: standardLine(
		'environment',
		'You go over the room once, properly. Nothing here was left FOR you — but you left something: {itemNames}.',
	),
	searchRoomEmpty: standardLine('environment', 'You go over the room once, properly. Nothing here wants going through.'),
	searchRoomSurvey: standardLine('environment', 'You go over the room once, properly. Worth going through on its own: {itemNames}.'),
	// D253: a shut door of mesh or glass — the search cannot go through it, and
	// says what can be seen through it instead of pretending nothing can.
	searchSeenThrough: standardLine('environment', 'The {itemName} is shut. Through it: {itemNames}.'),
	searchWhileShut: standardLine('environment', 'The {itemName} is shut. Whatever is in there stays in there for now.'),
	// D253: seen through a shut door of mesh or glass, and not reached. The
	// door is named because it is the barrier, and opening it is the answer.
	seenBehindShutDoor: standardLine('environment', 'The {itemName} is in {containerName}, behind a shut door.'),
	slotOccupied: standardLine('environment', 'You would have to take off the {wornItemName} first.'),
	socialInput: standardLine('environment', 'The silence accepts your courtesy without returning it.'),
	statementNotUnderstood: standardLine(
		'environment',
		'Noted, and filed nowhere. The world answers directives, not declarations.',
	),
	// D244: a stock that has given everything it counts, and one whose gift
	// would pass what the player can hold — placeholders until a pack (or
	// Calder) re-voices them; both say what refused, never a success.
	stockEmpty: standardLine('environment', 'There is nothing left to take from the {itemName}.'),
	stockFull: standardLine('environment', 'You have no room for another.'),
	// R11: a TERMINAL ending's generic refusal — an ending's own
	// `refusalNarratives` outrank it. Neutral register, engine voice: it names
	// the fact without naming the fiction, which is the pack's to claim.
	storyHasEnded: standardLine('system', 'THE STORY HAS ENDED. NOTHING FURTHER CHANGES.'),
	// D234: the player's strike — landed, or turned entirely by the foe's defense (the foe's `hurtNarratives` outrank the first).
	strikeAbsorbed: standardLine('environment', 'You strike the {itemName}, and it does not tell. Nothing you have gets through.'),
	strikeLanded: standardLine('environment', 'You strike the {itemName}, and it tells.'),
	structureFallback: standardLine(
		'environment',
		'The shape of that did not resolve into anything doable{tokens}.{suggestions}',
	),
	/*
	 * D120: the switch is already where it was asked to go. An action that
	 * changes nothing must SAY so — borrowing the transition's prose reports a
	 * movement that did not happen.
	 */
	switchAlreadyOff: standardLine('environment', 'The {itemName} is already off. The switch is where you would be putting it.'),
	switchAlreadyOn: standardLine('environment', 'The {itemName} is already running. The switch does not have anywhere else to go.'),
	switchedOff: standardLine('environment', 'The {itemName} goes quiet.'),
	switchedOn: standardLine('environment', 'The {itemName} comes on.'),
	switchIsLatched: standardLine('environment', 'The {itemName} has nothing left to bite. It went one way, once.'),
	// Deliberately unremarkable, and it is a hard constraint rather than an
	// oversight: the vertical slice's three-beat arc needs the take
	// acknowledgement plain, so that the handwritten log's MISSING take line
	// reads as silence rather than as a change of tone.
	takeAcknowledgement: standardLine('environment', 'You take the {itemName}.'),
	unboundPreposition: standardLine('environment', '{intent} has no use for "{tokens}" in that position.{suggestions}'),
	unequipped: standardLine('environment', 'You take off the {itemName}.'),
	unknownIntent: standardLine('environment', '{intent} is a word this world knows and cannot act on.'),
	// D35: recognized verbs the engine has no mechanics for. Naming the verb —
	// and the thing it was aimed at — is the difference between a refusal and
	// an accusation of gibberish.
	unsupportedIntent: standardLine('environment', '{intent} is understood here, and answered nowhere.'),
	// D44: the frame names the THING first and the verb second, because the
	// transitive frame it replaces ("You cannot {verb} the {itemName}") is
	// ungrammatical for every intransitive verb in the roster — "You cannot
	// listen the kettle." Naming the item first is correct for every verb
	// shape, keeps both specifics, and rhymes deliberately with
	// `entityNotFound`: this world answers to things, or does not.
	// D63/Calder §4: "Nothing about…" is NUMBER-AGNOSTIC — "The benches does
	// not answer" was the D44 template meeting a plural name, and re-phrasing
	// the frame beats authoring per-item entries on things with nothing to say.
	unsupportedIntentOnTarget: standardLine('environment', 'Nothing about the {itemName} answers to {intent}.'),
	verbCouldNotCarry: standardLine('environment', '{intent} could not carry "{tokens}" anywhere useful.{suggestions}'),
	// D78/Calder §8.5: this was the one question in the game written in the
	// voice of a user manual — shouted verb, angle brackets, syntax advice —
	// while its two siblings ("Go where?", "Use it on what?") were sentences a
	// world could say. It is also the most COMMON of the three, because a bare
	// verb is the most natural thing to type when you do not know what is
	// there. D41 already parks the verb, so the question it now asks is one the
	// player can answer with a bare noun; the teach rides along unchanged.
	// `{verb}` is SENTENCE case, so it cannot open a sentence — "take what?"
	// renders lowercase. `To {verb} what?` puts a literal capital in front and
	// is the phrasing a person actually uses out loud.
	verbNeedsTarget: standardLine('environment', 'To {verb} what? Name the thing and it will be tried.{suggestions}'),
	// D232: an item verb aimed at a body. Two clauses like its siblings: the
	// refusal, and the reason — a person is not a thing the verb takes. The
	// actor's own `verbNarratives` are where the real sentence lives ("She
	// would notice."); this is the floor under every pack that writes none.
	verbOnActor: standardLine('environment', 'The {itemName} is not a thing you {verb}. Not one that would let you, anyway.'),
	verbStandsAlone: standardLine('environment', '{intent} stands alone — it cannot take "{tokens}".{suggestions}'),
	// D196: an authored verb aimed at a thing that has no arm for it, or none
	// whose conditions hold. Number-agnostic like `unsupportedIntentOnTarget`
	// (D63), and just as flat on purpose: the arm an author writes without
	// conditions is where the real refusal lives.
	verbUnanswered: standardLine('environment', 'Nothing about the {itemName} answers to {intent}, not now.'),
	// D52: the sound of a turn going by on purpose. Neutral, and deliberately
	// slight — a game with a clock running should claim this slot and make the
	// pause mean something, which is the whole reason `wait` earns its turn.
	waitAcknowledgement: standardLine('environment', 'You wait. The world takes the moment and gives nothing back for it.'),
	// D234: `attack … with` a wieldable thing that is merely carried — D143's rule, a knife as the mask.
	weaponNotWielded: standardLine('environment', 'The {itemName} is not in your hand. Wield it first.'),
	// D143 addendum: the worn thing's name as the inventory lists it. A
	// marker on the existing line rather than a second line — the mask is
	// carried AND worn, and listing it twice would say so twice.
	wornMarker: standardLine('environment', '{itemName} (worn)'),
};

